<template>
  <div>
    <div v-if="addresses.length > 0" class="book">
      <h5 class="headline mb-1">
        {{ t.take_address }}
      </h5>

      <AddressChooser
        :address="initialAddress"
        :addresses="addresses"
        :form-name="formName"
        @change="setAddress"
      />
    </div>
    <div class="wrapper">
      <div class="row">
        <div class="col-xs-24">
          <FormInputFloatingLabel
            :label="t.company"
            :name="`${formName}[company]`"
            v-model="company"
          />
        </div>
      </div>

      <div class="row">
        <div class="col-xs-12">
          <FormInputFloatingLabel
            :label="t.firstname"
            :name="`${formName}[firstname]`"
            v-model="firstname"
            :required="true"
          />
        </div>
        <div class="col-xs-12">
          <FormInputFloatingLabel
            :label="t.lastname"
            :name="`${formName}[lastname]`"
            v-model="lastname"
            :required="true"
          />
        </div>
      </div>

      <div class="row">
        <div class="col-xs-16">
          <FormInputFloatingLabel
            :label="t.address1"
            :name="`${formName}[address1]`"
            v-model="address1"
            :required="true"
          />
        </div>
        <div class="col-xs-8">
          <FormInputFloatingLabel
            :label="t.street_number"
            :name="`${formName}[street_number]`"
            v-model="street_number"
            :required="true"
          />
        </div>
      </div>

      <div class="row">
        <div class="col-xs-24 col-sm-6">
          <FormInputFloatingLabel
            :label="t.zipcode"
            :name="`${formName}[zipcode]`"
            v-model="zipcode"
            :required="true"
            :prefix="(showZipCodePrefix && zipCodePrefix) || ''"
          />
        </div>
        <div
          :class="{ 'col-sm-10': showZipCodePrefix, 'col-sm-18': !showZipCodePrefix }"
          class="col-xs-24"
        >
          <FormInputFloatingLabel
            :label="t.city"
            :name="`${formName}[city]`"
            v-model="city"
            :required="true"
          />
        </div>
        <div v-if="showZipCodePrefix" class="col-xs-24 col-sm-8">
          <div class="form-group">
            <div class="w-100 position-absolute">
              <select
                :id="getRailsIdFromFieldName(`${formName}[country_id]`)"
                :name="`${formName}[country_id]`"
                v-model="currentCountry"
                class="form-control privacy"
              >
                <option
                  v-for="country in countries"
                  :key="country[1]"
                  :value="country[1]"
                  :data-zip-code-prefix="country[2]['data-prefix']"
                  >{{ country[0] }}</option
                >
              </select>
              <label :for="`${formName}[country]`">{{ t.country }}</label>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div :class="{ 'col-sm-12': showAdditionalInformation }" class="col-xs-24">
          <FormInputFloatingLabel
            :label="t.phone"
            :name="`${formName}[phone]`"
            v-model="phone"
            :required="true"
          />
        </div>
        <div v-if="showAdditionalInformation" class="col-xs-24 col-sm-12">
          <FormInputFloatingLabel
            :label="t.address2"
            :name="`${formName}[address2]`"
            v-model="address2"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import getRailsIdFromFieldName from "../mixins/getRailsIdFromFieldName";
import FormInputFloatingLabel from "./FormInputFloatingLabel";
import AddressChooser from "./AddressChooser";

export default {
  name: "CheckoutAddress",
  components: {
    FormInputFloatingLabel,
    AddressChooser,
  },
  mixins: [getRailsIdFromFieldName],
  props: {
    initialAddress: {
      type: Object,
      default() {
        return {};
      },
    },
    addresses: {
      type: Array,
      required: true,
    },
    formName: {
      type: String,
      required: true,
    },
    t: {
      type: Object,
      required: true,
    },
    showZipCodePrefix: {
      type: Boolean,
    },
    initialCountry: {
      type: Number,
      required: true,
    },
    countries: {
      type: Array,
      required: true,
    },
    showAdditionalInformation: {
      type: Boolean,
    },
  },
  data() {
    return {
      company: this.initialAddress.company,
      firstname: this.initialAddress.firstname,
      lastname: this.initialAddress.lastname,
      address1: this.initialAddress.address1,
      street_number: this.initialAddress.street_number,
      city: this.initialAddress.city,
      zipcode: this.initialAddress.zipcode,
      currentCountry: this.initialCountry,
      phone: this.initialAddress.phone,
      address2: this.initialAddress.address2,
      zipCodePrefix: null,
    };
  },
  watch: {
    currentCountry: {
      immediate: true,
      handler(newCountry) {
        const country = this.countries.find(country => country[1] == newCountry);

        if (country) this.zipCodePrefix = country[2]["data-prefix"];
      },
    },
  },
  methods: {
    setAddress(address) {
      this.company = address.company;
      this.firstname = address.firstname;
      this.lastname = address.lastname;
      this.address1 = address.address1;
      this.street_number = address.street_number;
      this.city = address.city;
      this.zipcode = address.zipcode;
      this.currentCountry = address.country_id;
      this.phone = address.phone;
      this.address2 = address.address2;
    },
  },
};
</script>
<style lang="scss" scoped>
@import "~styles/global";

.book {
  white-space: nowrap;
  overflow-x: auto;

  @media (min-width: $screen-sm-min) {
    margin-bottom: $spacer-2;
  }
}

.headline {
  font-size: $font-size-sm;
  font-weight: normal;
  color: $input-focus-border-color;
}

select {
  border: 0;
  border-bottom: $btn-focus-width solid $input-focus-border-color;
  margin-top: 1rem;
  padding: $spacer-1 0;
  border-radius: 0;
  height: $floating-form-select-height;
  outline: none;
  box-shadow: none;
}

select option {
  padding-left: 0;
}

select + label {
  @include truncate;

  color: $input-focus-border-color;
  cursor: text;
  font-size: $font-size-sm;
  font-weight: normal;
  position: absolute;
  top: 0;
  position: absolute;
}

.wrapper {
  .form-group {
    height: $floating-label-form-group-height;
    margin: 0 0 spacer(3);
    padding: 0;
    position: relative;
  }
}
</style>
